import React, { useState } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { currentLanguageAtom, changeLanguageAtom } from '../../../../atoms/shared/uiAtoms';
import { supportedLanguages, supportedLanguageOptions } from '../../../../lib/i18n';
import { IOSActionSheet, IOSActionSheetItem } from './IOSActionSheet';
import { trackSettingsEvents } from '../../../../lib/analyticsEnhanced';
import { languageManager } from '../../../../services/languageManager';
import { getUserLanguagePreference } from '../../../../utils/languageUtils';
import { useLanguageDisplayNames } from '../../../../hooks/useLanguageDisplayNames';

interface IOSLanguageSelectorProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * iOS 风格的语言选择器组件
 * 使用 ActionSheet 实现
 */
export const IOSLanguageSelector: React.FC<IOSLanguageSelectorProps> = ({
  isOpen,
  onClose
}) => {
  const { t } = useTranslation(['v3/common', 'v3/me']);
  const [currentLanguage] = useAtom(currentLanguageAtom);
  const [, changeLanguage] = useAtom(changeLanguageAtom);
  const { getLanguageDisplayName } = useLanguageDisplayNames();

  // 选择语言
  const handleSelectLanguage = async (langCode: string) => {
    const oldLanguage = currentLanguage;
    const oldPreference = getUserLanguagePreference();

    try {
      // 记录语言变更事件
      trackSettingsEvents.languageChanged(oldPreference || oldLanguage, langCode, {
        trigger: 'language_selector',
        available_languages: Object.keys(supportedLanguageOptions)
      });

      // 使用 languageManager 设置语言偏好
      await languageManager.setUserLanguagePreference(langCode);
      onClose();
    } catch (error) {
      console.error('Failed to set language preference:', error);
      // 回退到原来的方法
      changeLanguage(langCode as keyof typeof supportedLanguages);
      onClose();
    }
  };

  // 获取当前用户语言偏好
  const currentUserPreference = getUserLanguagePreference() || 'system';

  // 将语言选项转换为ActionSheet项目
  const languageItems: IOSActionSheetItem[] = Object.entries(supportedLanguageOptions).map(([code, name]) => ({
    id: code,
    label: getLanguageDisplayName(code),
    isSelected: code === currentUserPreference,
    onClick: () => handleSelectLanguage(code)
  }));

  return (
    <IOSActionSheet
      isOpen={isOpen}
      onClose={onClose}
      items={languageItems}
      title={t('v3/me:appearance.language')}
    />
  );
};

export default IOSLanguageSelector;
